import { useEffect, useMemo, useState } from "react";
import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { ItineraryActivity } from "../models/activity.model";
import {
  useActivityLibraryQuery,
  useSaveActivityToLibrary,
} from "@/lib/library/hooks/useActivityLibrary";
import { useLibraryScope } from "@/lib/library/hooks/useLibraryScope";
import { LibraryActivityFactory } from "@/lib/library/models/library-activity.model";
import { TagInput } from "@/lib/library/components/TagInput";

interface SaveToLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Attività del builder da salvare in libreria; null quando il modal è chiuso. */
  activity: ItineraryActivity | null;
}

/**
 * Modal per scegliere i tag prima di salvare un'attività del builder in libreria.
 * I tag esistenti (derivati dalle entry già presenti in libreria, stesso approccio di
 * LibraryActivitiesTable) sono proposti come chip selezionabili; è comunque possibile
 * crearne di nuovi al volo tramite TagInput.
 *
 * L'ambito di salvataggio è implicito e non richiede scelte all'utente:
 * - account `agency`: salva sempre direttamente in libreria agenzia
 *   (`p_agency_id = <mia agenzia> = user.id`);
 * - tutti gli altri (membro agenzia, utente semplice): salva in libreria personale
 *   (`p_agency_id = null`). Il membro potrà eventualmente promuovere la entry in
 *   agenzia dalla pagina Libreria (vedi `promote_activity_to_agency`).
 */
export function SaveToLibraryModal({ isOpen, onClose, activity }: SaveToLibraryModalProps) {
  const { t } = useI18n();
  const { user } = useAuth();
  const { toast } = useToast();
  const { isAgencyAccount, agencyId } = useLibraryScope();
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const { data: libraryActivities = [] } = useActivityLibraryQuery();
  const saveActivityToLibrary = useSaveActivityToLibrary();

  const availableTags = useMemo(() => {
    const tagSet = new Set<string>();
    libraryActivities.forEach((entry) => entry.tags.forEach((tag) => tagSet.add(tag)));
    return Array.from(tagSet).sort();
  }, [libraryActivities]);

  // Riparte da una selezione pulita ogni volta che il modal si riapre per una
  // nuova attività.
  useEffect(() => {
    if (isOpen) {
      setSelectedTags([]);
    }
  }, [isOpen, activity?.id]);

  const handleSave = () => {
    if (!activity) return;
    if (!user?.id) {
      console.error('Cannot save activity to library: missing current user id');
      toast({
        title: t('common.error'),
        description: t('library.saveErrorDescription'),
        variant: 'destructive',
      });
      return;
    }

    // Ambito implicito: l'account agency salva sempre in agenzia (agencyId = user.id),
    // tutti gli altri salvano in personale (agency_id null).
    const saveAgencyId: string | null = isAgencyAccount ? agencyId : null;

    const payload = LibraryActivityFactory.fromItineraryActivity(activity, { tags: selectedTags });
    saveActivityToLibrary.mutate(
      {
        activity: payload,
        agencyId: saveAgencyId,
      },
      {
        onSuccess: () => onClose(),
      }
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('library.saveToLibraryModalTitle')}
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={!activity || saveActivityToLibrary.isPending}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="p-4 flex flex-col gap-4">
        {activity && (
          <div>
            <p className="text-xs text-gray-500">{t('library.saveToLibraryModalActivityLabel')}</p>
            <p className="font-semibold text-gray-900">{activity.title}</p>
          </div>
        )}

        <TagInput
          availableTags={availableTags}
          selectedTags={selectedTags}
          onChange={setSelectedTags}
        />
      </div>
    </Modal>
  );
}
